import { z } from 'zod';
import { Id, CanonicalId, Digest, Property, ResolveInput } from './contracts.mjs';
import { requireCondition } from './errors.mjs';
import { normalizeImagePresentation } from './image-presentation.mjs';
import {
  NumberFormat,
  PatchValue,
  normalizeNumberFormat,
  normalizeColourToken,
  normalizeSpacing,
} from '../../artifacts/presentation-values.mjs';

export { NumberFormat, PatchValue };
export const ApplyPatchInput = z.strictObject({
  operationId: Id,
  operationDigest: Digest,
  hostIntent: Id,
});
export const UndoPatchInput = z.strictObject({
  operationId: Id,
  expectedRevisionId: CanonicalId,
  hostIntent: Id,
  idempotencyKey: Id,
});
const identity = { slotId: Id, property: Property, oldValue: PatchValue };
export const Change = z.union([
  z.strictObject({ ...identity, newValue: PatchValue }),
  z.strictObject({ ...identity, action: z.literal('clear-override') }),
]);
export const PreparePatchInput = z.union([
  ResolveInput.extend({ changes: z.array(Change).min(1).max(128), idempotencyKey: Id }),
  z.strictObject({
    selectionSetId: Id,
    changes: z.array(Change).min(1).max(128),
    idempotencyKey: Id,
  }),
]);
export function normalizePatchValue(property, input, tokens, slot) {
  const validators = {
    text: normalizeText,
    colour: (value, policy) => normalizeColour(value, policy, slot),
    spacing: (value, policy) => normalizeSpacing(value, policy.spacing),
    numberFormat: normalizeNumberFormat,
    imageFit: (value) => normalizeImagePresentation('imageFit', value, slot),
    imageRatio: (value) => normalizeImagePresentation('imageRatio', value, slot),
  };
  return validators[property](input, tokens);
}
function normalizeText(value) {
  requireCondition(
    typeof value === 'string' && value.length <= 10000 && !value.includes('\0'),
    'invalid_text',
  );
  return value;
}
function normalizeColour(value, tokens, slot) {
  const key = normalizeColourToken(value, tokens.colours);
  if (slot?.sourceKind === 'artifact-node') return key;
  requireCondition(typeof tokens.colours[key] === 'string', 'invalid_colour_token_value');
  return tokens.colours[key];
}
